import { memo, useEffect } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";

import { useNotificationSocket } from "@/hooks/useNotificationSocket";
import { cn } from "@/lib/utils";
import { useCustomerAuthStore } from "@/stores/customerAuthStore";

import styles from "../AppLayout.module.scss";
import { BottomNav } from "./BottomNav";
import { Header } from "./Header";
import { AddToHomeScreen } from "./AddToHomeScreen";

export const AppCustomerLayout = memo(() => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { initAuth, logout } = useCustomerAuthStore();

  // Initialize auth state on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Listen for global auth:logout event
  useEffect(() => {
    const handleLogout = () => {
      logout();
    };
    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, [logout]);

  // Connect to notification socket
  useNotificationSocket();

  const isLogin = location.pathname === "/login";
  const isMembership = location.pathname === "/profile/membership";
  const isNotification = location.pathname === "/notifications";
  const isSetting = location.pathname === "/settings";
  const isModalOpen = !!searchParams.get("voucherId");
  const isHardVoucherPage = location.pathname.startsWith("/vouchers/");
  const isProfileSubPage =
    location.pathname.startsWith("/profile/edit") ||
    location.pathname.startsWith("/profile/change-password") ||
    location.pathname.startsWith("/profile/transactions") ||
    location.pathname.startsWith("/profile/referral") ||
    location.pathname.startsWith("/profile/faqs") ||
    location.pathname.startsWith("/terms") ||
    location.pathname.startsWith("/privacy-policy") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/contact-us");
  const isArticlePage = location.pathname.startsWith("/articles/");
  const isStandalonePage =
    isHardVoucherPage || isProfileSubPage || isArticlePage;

  const shouldHideNav =
    isStandalonePage || isModalOpen || isMembership || isNotification;
  const showContainer = !isStandalonePage;

  const containerClass = "max-w-[830px] mx-auto w-full px-4 md:px-10";

  return (
    <div className={styles["app-layout"]}>
      <AddToHomeScreen />
      {!shouldHideNav && <Header containerClass={containerClass} />}
      <main
        className={cn(
          styles["main-content"],
          showContainer && containerClass,
          showContainer && "pt-[10px]",
          !isLogin && "pb-[90px]",
          (isProfileSubPage || isArticlePage) && "!pb-0",
          (isNotification || isMembership || isSetting) && "bg-white",
        )}
        role="main"
      >
        <Outlet />
      </main>
      {!shouldHideNav && <BottomNav />}
    </div>
  );
});

AppCustomerLayout.displayName = "AppCustomerLayout";
